'use client'

import { RefObject, useEffect, useRef } from 'react'
import { omit } from '@nwwp/utils'
import { useIsElementInViewport } from './use-is-element-in-viewport'
import { useStandardTrack } from './use-standard-track'

type Props = {
  element: string
  href?: string
} & Record<string, unknown>

export const useTrackElementViewed = (elementRef: RefObject<HTMLElement | null>, params: Props) => {
  const track = useStandardTrack()
  const impressionEmitted = useRef(false)
  // Mint on mount so clicks before IO can still pair via parent_event_id.
  const viewEventId = useRef<string>(crypto.randomUUID())
  const prevIdentityRef = useRef(`${params.element}\0${params.href ?? ''}`)
  const paramsRef = useRef(params)
  paramsRef.current = params
  const isElementInViewport = useIsElementInViewport(elementRef, {
    threshold: 0,
  })

  const identity = `${params.element}\0${params.href ?? ''}`
  if (prevIdentityRef.current !== identity) {
    prevIdentityRef.current = identity
    impressionEmitted.current = false
    viewEventId.current = crypto.randomUUID()
  }

  useEffect(() => {
    if (!isElementInViewport || impressionEmitted.current) return

    impressionEmitted.current = true
    const current = paramsRef.current
    const eventId = viewEventId.current
    const destination = typeof current.href === 'string' ? current.href : undefined
    const rest = omit(current, ['element', 'href', 'event_id'])

    if (destination) {
      track('Link Viewed', {
        ...rest,
        entity_name: current.element,
        destination_url: destination,
        link_copy: current.element,
        event_id: eventId,
        element: current.element,
        href: destination,
      })
      return
    }

    track('Element Viewed', {
      ...rest,
      entity_name: current.element,
      event_id: eventId,
      element: current.element,
    })
  }, [track, isElementInViewport, elementRef, params.element, params.href])

  return viewEventId
}
